import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import BoltIcon from "@mui/icons-material/Bolt";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ImageIcon from "@mui/icons-material/Image";
import api from "../../api/api";
import { clearCache } from "../../utils/cache";
import { useSnackbar } from "../../utils/snackbar";

const PAGE_SIZE = 20;
const MAX_QUICK_PICKS = 50;
const PRODUCTS_CACHE_KEY = "admin_products_cache_v1";
const BRAND_GRAD = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

function ProductThumb({ src, title, size = 48 }) {
  if (!src) {
    return (
      <Avatar
        variant="rounded"
        sx={{ width: size, height: size, bgcolor: "action.hover" }}
      >
        <ImageIcon sx={{ color: "text.disabled", fontSize: size * 0.5 }} />
      </Avatar>
    );
  }
  return (
    <Avatar
      variant="rounded"
      src={src}
      alt={title}
      sx={{ width: size, height: size, bgcolor: "action.hover" }}
    />
  );
}

export default function AdminQuickPicks() {
  const theme = useTheme();
  const { showAlert } = useSnackbar();

  const [pageProducts, setPageProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // { [productId]: { isQuickPick: bool, quickPickOrder: number | null } }
  const [selections, setSelections] = useState({});

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);

  /* ── Load products ── */
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/api/products", {
          params: { page, limit: PAGE_SIZE, search },
        });
        if (!active) return;

        const incoming = res.data.data || [];
        setPageProducts(incoming);
        setMeta(res.data.meta);

        setSelections((prev) => {
          const next = { ...prev };
          incoming.forEach((p) => {
            if (!(p._id in next)) {
              next[p._id] = {
                isQuickPick: !!p.isQuickPick,
                quickPickOrder: p.quickPickOrder ?? null,
              };
            }
          });
          return next;
        });
      } catch {
        showAlert("Failed to load products", "error");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, search]);

  /* ── Merge server data + local selections ── */
  const paginated = useMemo(
    () =>
      pageProducts.map((p) => {
        const sel = selections[p._id];
        if (!sel) return p;
        return { ...p, isQuickPick: sel.isQuickPick, quickPickOrder: sel.quickPickOrder };
      }),
    [pageProducts, selections]
  );

  const pickCount = useMemo(
    () => Object.values(selections).filter((s) => s?.isQuickPick).length,
    [selections]
  );

  /* ── Active quick picks list ── */
  const activePickList = useMemo(() => {
    const byId = Object.fromEntries(pageProducts.map((p) => [p._id, p]));
    return Object.entries(selections)
      .filter(([, s]) => s?.isQuickPick)
      .map(([id, s]) => ({ id, product: byId[id] || null, sel: s }))
      .filter((x) => x.product)
      .sort((a, b) => (a.sel.quickPickOrder ?? 999) - (b.sel.quickPickOrder ?? 999));
  }, [selections, pageProducts]);

  const totalPages = meta?.pages || 1;
  const progressPct = (pickCount / MAX_QUICK_PICKS) * 100;

  /* ── Bulk selection ── */
  const visibleIds = paginated.map((p) => p._id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selectedIds.includes(id));

  const handleHeaderCheckbox = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => {
        const added = visibleIds.filter((id) => !prev.includes(id));
        return [...prev, ...added];
      });
    }
  };

  const handleRowCheckbox = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ── Bulk actions ── */
  const bulkEnableAll = () => {
    setSelections((prev) => {
      const usedOrders = new Set(
        Object.entries(prev)
          .filter(([pid, s]) => s?.isQuickPick && !selectedIds.includes(pid))
          .map(([, s]) => Number(s.quickPickOrder))
          .filter(Boolean)
      );
      let nextOrder = usedOrders.size > 0 ? Math.max(...usedOrders) + 1 : 1;
      const next = { ...prev };
      for (const id of selectedIds) {
        while (usedOrders.has(nextOrder)) nextOrder++;
        usedOrders.add(nextOrder);
        next[id] = { isQuickPick: true, quickPickOrder: nextOrder };
        nextOrder++;
      }
      return next;
    });
    setSelectedIds([]);
  };

  const bulkDisableAll = () => {
    setSelections((prev) => {
      const next = { ...prev };
      for (const id of selectedIds) {
        next[id] = { ...next[id], isQuickPick: false, quickPickOrder: null };
      }
      return next;
    });
    setSelectedIds([]);
  };

  /* ── Toggle ── */
  const togglePick = (id, checked) => {
    setSelections((prev) => {
      const usedOrders = new Set(
        Object.entries(prev)
          .filter(([pid, s]) => s?.isQuickPick && pid !== id)
          .map(([, s]) => Number(s.quickPickOrder))
          .filter(Boolean)
      );
      let nextOrder = 1;
      while (usedOrders.has(nextOrder)) nextOrder++;

      if (checked && nextOrder > MAX_QUICK_PICKS) {
        showAlert(`Maximum of ${MAX_QUICK_PICKS} quick picks reached.`, "error");
        return prev;
      }
      return {
        ...prev,
        [id]: { isQuickPick: checked, quickPickOrder: checked ? nextOrder : null },
      };
    });
  };

  const setOrder = (id, rawOrder) => {
    const order = Number(rawOrder);
    if (!Number.isInteger(order) || order < 1 || order > MAX_QUICK_PICKS) {
      showAlert(`Order must be between 1 and ${MAX_QUICK_PICKS}.`, "error");
      return;
    }
    setSelections((prev) => {
      const clash = Object.entries(prev).some(
        ([pid, s]) => pid !== id && s?.isQuickPick && Number(s.quickPickOrder) === order
      );
      if (clash) {
        showAlert(`Order ${order} is already in use.`, "error");
        return prev;
      }
      return { ...prev, [id]: { ...prev[id], quickPickOrder: order } };
    });
  };

  const removeFromPicks = (id) => togglePick(id, false);

  /* ── Save ── */
  const saveQuickPicks = async () => {
    const picks = Object.entries(selections)
      .filter(([, s]) => s?.isQuickPick)
      .map(([id, s]) => ({ id, order: Number(s.quickPickOrder) }));

    if (picks.length > MAX_QUICK_PICKS) {
      showAlert(`Maximum of ${MAX_QUICK_PICKS} quick picks allowed.`, "error");
      return;
    }
    for (const p of picks) {
      if (!p.order || p.order < 1 || p.order > MAX_QUICK_PICKS) {
        showAlert(`Order must be between 1 and ${MAX_QUICK_PICKS}.`, "error");
        return;
      }
    }
    const orders = picks.map((p) => p.order);
    const dupes = orders.filter((o, i) => orders.indexOf(o) !== i);
    if (dupes.length) {
      showAlert(`Duplicate order found (${dupes[0]}). Each pick needs a unique order.`, "error");
      return;
    }

    setSaving(true);
    try {
      await api.put("/api/admin/products/quick-picks", { products: picks });
      clearCache(PRODUCTS_CACHE_KEY);
      showAlert("Quick Picks updated successfully!", "success");
    } catch (err) {
      showAlert(
        err?.response?.data?.message || err?.message || "Failed to save quick picks.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ── Skeleton rows ── */
  const SkeletonRows = () =>
    [...Array(6)].map((_, i) => (
      <TableRow key={i}>
        <TableCell colSpan={6}>
          <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
        </TableCell>
      </TableRow>
    ));

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1500, mx: "auto" }}>

        {/* ── HEADER ── */}
        <Fade in timeout={400}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              bgcolor: "background.paper",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack spacing={3}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={2}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      background: BRAND_GRAD,
                      borderRadius: 2,
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <AutoAwesomeIcon sx={{ color: "white", fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ background: BRAND_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                    >
                      Quick Picks
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      Curate fast-favourite products shown in the home screen horizontal scroll
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Tooltip title="Quick Picks appear in the home screen's horizontal scroll. When none are set, the section falls back to most-viewed products.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={saveQuickPicks}
                    disabled={saving}
                    sx={{
                      background: BRAND_GRAD,
                      boxShadow: "0 4px 14px rgba(102,126,234,0.45)",
                      px: 3,
                      py: 1.25,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { background: "linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)" },
                    }}
                  >
                    {saving ? "Saving…" : "Save Changes"}
                  </Button>
                </Stack>
              </Stack>

              <Divider />

              {/* Slot counter */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Slots used
                  </Typography>
                  <Chip
                    label={`${pickCount} / ${MAX_QUICK_PICKS}`}
                    size="small"
                    sx={{
                      background: progressPct > 80 ? "linear-gradient(135deg,#f093fb,#f5576c)" : BRAND_GRAD,
                      color: "white",
                      fontWeight: 700,
                    }}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(progressPct, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 5,
                    bgcolor: "action.hover",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      background: progressPct > 80 ? "linear-gradient(90deg,#f093fb,#f5576c)" : BRAND_GRAD,
                    },
                  }}
                />
              </Box>
            </Stack>
          </Card>
        </Fade>

        {/* ── TWO-PANEL LAYOUT ── */}
        <Grid container spacing={3}>

          {/* LEFT — catalog browser */}
          <Grid item xs={12} lg={7}>
            <Stack spacing={2}>

              {/* Search */}
              <Card elevation={0} sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search products by name or category…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Card>

              {/* Bulk action bar */}
              {selectedIds.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: "action.selected",
                    border: "1.5px solid",
                    borderColor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Chip
                    label={`${selectedIds.length} selected`}
                    size="small"
                    sx={{ background: BRAND_GRAD, color: "white", fontWeight: 700 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={bulkEnableAll}
                    sx={{ background: BRAND_GRAD, textTransform: "none", fontWeight: 600, borderRadius: 1.5, boxShadow: "none" }}
                  >
                    Enable All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={bulkDisableAll}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                  >
                    Disable All
                  </Button>
                  <Box sx={{ ml: "auto" }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedIds([])}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Product table */}
              <Paper elevation={0} sx={{ overflow: "hidden", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell padding="checkbox" width={44}>
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={someVisibleSelected}
                          onChange={handleHeaderCheckbox}
                          disabled={loading || paginated.length === 0}
                        />
                      </TableCell>
                      <TableCell width={56} />
                      <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                      <TableCell width={100} sx={{ fontWeight: 700 }}>Quick Pick</TableCell>
                      <TableCell width={100} sx={{ fontWeight: 700 }}>Order</TableCell>
                      <TableCell width={110} sx={{ fontWeight: 700 }}>Stock</TableCell>
                      <TableCell width={150} sx={{ fontWeight: 700 }}>Category</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <SkeletonRows />
                    ) : paginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 6 }}>
                          <Stack alignItems="center" spacing={1.5}>
                            <SearchIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                            <Typography variant="body2" color="text.secondary">
                              No products found
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              Try adjusting your search
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginated.map((p) => {
                        const imgSrc = Array.isArray(p.images) && p.images.length > 0
                          ? p.images[0]
                          : p.imageUrl || null;

                        return (
                          <TableRow
                            key={p._id}
                            hover
                            selected={selectedIds.includes(p._id)}
                            sx={{
                              borderLeft: p.isQuickPick ? "3px solid" : "3px solid transparent",
                              borderLeftColor: p.isQuickPick ? "primary.main" : "transparent",
                              transition: "background 0.15s ease",
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={selectedIds.includes(p._id)}
                                onChange={() => handleRowCheckbox(p._id)}
                              />
                            </TableCell>

                            <TableCell sx={{ py: 1 }}>
                              <ProductThumb src={imgSrc} title={p.title} size={40} />
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }}>
                                {p.title}
                              </Typography>
                              {p.price != null && (
                                <Typography variant="caption" color="text.secondary">
                                  KES {Number(p.price).toLocaleString()}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Checkbox
                                size="small"
                                checked={!!p.isQuickPick}
                                disabled={!p.isQuickPick && pickCount >= MAX_QUICK_PICKS}
                                onChange={(e) => togglePick(p._id, e.target.checked)}
                                icon={<BoltOutlinedIcon fontSize="small" />}
                                checkedIcon={<BoltIcon fontSize="small" sx={{ color: "#fbbf24" }} />}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                disabled={!p.isQuickPick}
                                value={p.quickPickOrder || ""}
                                onChange={(e) => setOrder(p._id, Number(e.target.value))}
                                inputProps={{ min: 1, max: MAX_QUICK_PICKS }}
                                placeholder="Order"
                                sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={
                                  p.stock > 0 ? `${p.stock} in stock` : "Out of stock"
                                }
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  bgcolor: p.stock > 0 ? "success.light" : "error.light",
                                  color: p.stock > 0 ? "success.dark" : "error.dark",
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              {p.category?.name ? (
                                <Chip
                                  label={p.category.name}
                                  size="small"
                                  sx={{
                                    bgcolor: "action.selected",
                                    fontWeight: 500,
                                    borderRadius: 1.5,
                                  }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {meta
                      ? `${(meta.page - 1) * PAGE_SIZE + 1}–${Math.min(
                          (meta.page - 1) * PAGE_SIZE + pageProducts.length,
                          meta.total
                        )} of ${meta.total}`
                      : `${pageProducts.length} products`}
                  </Typography>
                  <Pagination
                    size="small"
                    page={page}
                    count={totalPages}
                    onChange={(_, p) => setPage(p)}
                    shape="rounded"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        background: BRAND_GRAD,
                        color: "white",
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Stack>
          </Grid>

          {/* RIGHT — current quick picks list */}
          <Grid item xs={12} lg={5}>
            <Card
              elevation={0}
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                position: { lg: "sticky" },
                top: { lg: 24 },
              }}
            >
              <Box sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Quick Picks List
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sorted by display order
                    </Typography>
                  </Box>
                  <Chip
                    label={`${pickCount} / ${MAX_QUICK_PICKS}`}
                    size="small"
                    sx={{ background: BRAND_GRAD, color: "white", fontWeight: 700 }}
                  />
                </Stack>
              </Box>

              {activePickList.length === 0 ? (
                <Box sx={{ py: 8, textAlign: "center", px: 3 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No quick picks selected yet
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Click the bolt icon in the table to add products
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 500, overflowY: "auto" }}>
                  {activePickList.map(({ id, product, sel }) => {
                    const imgSrc = Array.isArray(product.images) && product.images.length > 0
                      ? product.images[0]
                      : product.imageUrl || null;

                    return (
                      <Box
                        key={id}
                        sx={{
                          px: 2,
                          py: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          "&:last-child": { borderBottom: 0 },
                          "&:hover": { bgcolor: "action.hover" },
                          transition: "background 0.15s",
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.disabled"
                          sx={{ minWidth: 24, textAlign: "center" }}
                        >
                          #{sel.quickPickOrder}
                        </Typography>
                        <ProductThumb src={imgSrc} title={product.title} size={44} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {product.title}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" mt={0.25}>
                            {product.price != null && (
                              <Typography variant="caption" color="text.secondary">
                                KES {Number(product.price).toLocaleString()}
                              </Typography>
                            )}
                            <Chip
                              label={product.stock > 0 ? "In Stock" : "Out"}
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: 9,
                                fontWeight: 700,
                                bgcolor: product.stock > 0 ? "success.light" : "error.light",
                                color: product.stock > 0 ? "success.dark" : "error.dark",
                              }}
                            />
                          </Stack>
                        </Box>
                        <Tooltip title="Remove from quick picks">
                          <IconButton
                            size="small"
                            onClick={() => removeFromPicks(id)}
                            sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                          >
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {activePickList.length > 0 && (
                <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<SaveIcon />}
                    onClick={saveQuickPicks}
                    disabled={saving}
                    sx={{
                      background: BRAND_GRAD,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    {saving ? "Saving…" : "Save Quick Picks"}
                  </Button>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
